import * as Sentry from '@sentry/react';
import { redirect } from '@tanstack/react-router';

import { ExperimentApplied } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import type { defaultFeatureFlags } from 'src/utils/featureFlags';

type CheckoutV2EntrypointFeatureFlags = Pick<
  typeof defaultFeatureFlags,
  'EnableCheckoutV2' | 'EnableCheckoutV2EntrypointRedirect'
>;

type CheckoutEntrypointSearch = {
  redirect_status?: string;
};

const EXPERIMENT_ID = 'checkout-v2-entrypoint-redirect';

// trackExposure records which arm of the entrypoint-redirect rollout a user
// landed in, so it can be analyzed in Amplitude and correlated with errors
// in Sentry.
const trackExposure = (redirected: boolean) => {
  const variant = redirected ? 'test' : 'control';

  // The tracker only records events for users who have consented to tracking.
  tracker.trackEvent(
    new ExperimentApplied({
      experiment_id: EXPERIMENT_ID,
      experiment_variant: variant,
    })
  );
  Sentry.setTag('experimentName', `${EXPERIMENT_ID}:${variant}`);
  Sentry.setTag('experimentId', EXPERIMENT_ID);
};

// maybeRedirectCheckoutEntrypointToV2 redirects the user to the new checkout
// if it's enabled and LaunchDarkly says so.
export const maybeRedirectCheckoutEntrypointToV2 = ({
  featureFlags,
  search,
}: {
  featureFlags: CheckoutV2EntrypointFeatureFlags;
  search?: CheckoutEntrypointSearch;
}) => {
  // If the user is returning from a failed payment, we don't want to redirect
  // them to the new checkout.
  if (search?.redirect_status) {
    return;
  }

  // The redirect is only meaningful once Checkout V2 itself is enabled.
  if (!featureFlags.EnableCheckoutV2) {
    return;
  }

  const shouldRedirect = featureFlags.EnableCheckoutV2EntrypointRedirect;
  trackExposure(shouldRedirect);

  if (!shouldRedirect) {
    return;
  }

  // New checkout!
  throw redirect({
    to: '/store/checkout',
    replace: true,
  });
};
